import { ApexOptions } from 'apexcharts';
import { BeatLoader } from 'react-spinners';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import useDimensions from '../hooks/useDimension';
import { useData } from '../context/data.context';
import { getThresholds } from '../_helpers/dashboardHelper';
import { DeviceType, ParametersType, PriorityList } from '../constants/telosair-interfaces';
import { COLOR_BY_PRIORITY, GRAPH_COLORS } from '../constants/global-constants';

const ZOOMED_FONT_SIZE = '16px';
const DEFAULT_FONT_SIZE = '12px';

type Props = {
  data: {
    name: string;
    data: { x: string; y: number | null }[];
  }[];
  resizing: boolean;
  isDataLoading: boolean;
  parameter: string;
  isZoomed?: boolean;
  type: DeviceType;
};

/**
 * ApexHeatMap component.
 * @param props - The data to be displayed in the heatmap.
 * @returns The rendered component.
 */
export default function ApexHeatMap(props: Props): ReactElement {
  const { data, resizing, isDataLoading, parameter, isZoomed, type } = props;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { height, width } = useDimensions(chartContainerRef);
  const { clientThresholdSettings, userConfigDataV2 } = useData();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<{
    series: { name: string; data: { x: string; y: number | null }[] }[];
    options: ApexOptions;
  }>({
    series: [],
    options: {
      chart: {
        type: 'heatmap' as const
      },
      dataLabels: {
        enabled: false
      },
      colors: [],
      title: {
        text: ''
      }
    }
  });

  useEffect(() => {
    const thresholds = getThresholds(
      parameter as ParametersType,
      clientThresholdSettings,
      userConfigDataV2,
      type
    );

    const ranges = [
      {
        from: thresholds.min,
        to: thresholds.lowerCritical,
        color: COLOR_BY_PRIORITY[PriorityList.critical],
        name: PriorityList.critical
      },
      {
        from: thresholds.lowerCritical,
        to: thresholds.lowerWarning,
        color: COLOR_BY_PRIORITY[PriorityList.medium],
        name: PriorityList.medium
      },
      {
        from: thresholds.lowerWarning,
        to: thresholds.upperWarning,
        color: COLOR_BY_PRIORITY[PriorityList.low],
        name: PriorityList.low
      },
      {
        from: thresholds.upperWarning,
        to: thresholds.upperCritical,
        color: COLOR_BY_PRIORITY[PriorityList.medium],
        name: PriorityList.medium
      },
      {
        from: thresholds.upperCritical,
        to: thresholds.max,
        color: COLOR_BY_PRIORITY[PriorityList.critical],
        name: PriorityList.critical
      },
      {
        from: -9999,
        to: -9999,
        color: COLOR_BY_PRIORITY[PriorityList.none],
        name: PriorityList.none
      }
    ];

    // Transform data to handle null values
    const transformedData = data.map((series) => ({
      name: series.name,
      data: series.data.map((point) => ({
        x: point.x,
        y: point.y === null ? -9999 : point.y // Use -9999 as sentinel value for null
      }))
    }));

    setState({
      series: transformedData,
      options: {
        chart: {
          height,
          redrawOnWindowResize: true,
          redrawOnParentResize: true,
          toolbar: { show: false },
          parentHeightOffset: 0
        },
        xaxis: {
          labels: {
            style: {
              colors: GRAPH_COLORS.level,
              fontSize: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE,
              fontFamily: 'Lato',
              fontWeight: 400
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: GRAPH_COLORS.level,
              fontSize: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE,
              fontFamily: 'Lato',
              fontWeight: 400
            }
          }
        },
        grid: {
          show: false
        },
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: {
          width: 0.5
        },
        plotOptions: {
          heatmap: {
            colorScale: { ranges },
            radius: 1,
            useFillColorAsStroke: false,
            distributed: false
          }
        }
      }
    });
  }, [props, height, clientThresholdSettings, userConfigDataV2]);

  useEffect(() => {
    let timeout: any;
    if (height) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(false);
      }, 700);
    }
    return () => clearTimeout(timeout);
  }, [height, resizing]);

  return (
    <div className="flex-1 flex justify-between ">
      <div
        ref={chartContainerRef}
        className="flex flex-1 justify-center items-center w-full overflow-hidden"
      >
        {(isDataLoading || loading) && <BeatLoader color="#36d7b7" />}
        {!isDataLoading && !loading && (
          <div style={{ height, width }} className="overflow-hidden">
            <ReactApexChart
              options={state.options}
              series={state.series}
              type="heatmap"
              height={height}
              width={width}
            />
          </div>
        )}
      </div>
    </div>
  );
}
